import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../App';
import { SettingsSubPageWrapper } from '../Settings';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { UserAccessFields } from '../../components/users/UserAccessFields';
import { Users as UsersIcon, Shield, Edit, Mail, Plus, Trash2, Send } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UsersSettings() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({
    name: '', email: '', password: '', role: 'staff', branch_id: '', supplier_id: '', branch_ids: null, supplier_ids: null, permissions: null
  });
  const [perms, setPerms] = useState({ catalog: [], roles: {} });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [usersRes, permsRes, branchesRes, suppliersRes] = await Promise.all([
        axios.get(`${API}/users`, { withCredentials: true }),
        axios.get(`${API}/permissions`, { withCredentials: true }),
        axios.get(`${API}/branches`, { withCredentials: true }),
        axios.get(`${API}/suppliers`, { withCredentials: true })
      ]);
      setUsers(usersRes.data);
      setBranches(branchesRes.data);
      setSuppliers(suppliersRes.data);
      setPerms(permsRes.data);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  const accessPayload = () => ({
    branch_id: formData.branch_ids?.[0] || (formData.branch_ids === null ? formData.branch_id || null : null),
    supplier_id: formData.role === 'supplier' ? formData.supplier_id || null : null,
    ...(formData.role !== 'master_admin' && formData.role !== 'supplier'
      ? { branch_ids: formData.branch_ids, supplier_ids: formData.supplier_ids, permissions: formData.permissions }
      : { branch_ids: null, supplier_ids: null, permissions: null }),
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if ((formData.branch_ids && !formData.branch_ids.length) || (formData.supplier_ids && !formData.supplier_ids.length)) {
      toast.error('Tick at least one branch and supplier, or choose All'); return;
    }
    try {
      if (isCreating) {
        const res = await axios.post(`${API}/users/create`, {
          email: formData.email,
          name: formData.name,
          password: formData.password,
          role: formData.role,
          ...accessPayload()
        }, { withCredentials: true });
        if (formData.password) toast.success('User created');
        else if (res.data.invite_sent) toast.success(`User created. Invite emailed to ${res.data.email}`);
        else toast.warning('User created, but the invite email failed. Use the send button to retry.');
      } else if (editingUser) {
        await axios.put(`${API}/users/${editingUser.user_id}`, {
          name: formData.name,
          email: formData.email,
          ...(formData.password ? { password: formData.password } : {}),
          role: formData.role,
          ...accessPayload()
        }, { withCredentials: true });
        toast.success('User updated');
      }
      setIsModalOpen(false);
      setEditingUser(null);
      setIsCreating(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save user');
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setIsCreating(false);
    setFormData({
      name: user.name || '',
      email: user.email || '',
      password: '',
      role: user.role || 'staff',
      branch_id: user.branch_id || '',
      supplier_id: user.supplier_id || '',
      branch_ids: user.branch_ids ?? (user.role === 'manager' && user.branch_id ? [user.branch_id] : null),
      supplier_ids: user.supplier_ids ?? null,
      permissions: user.permissions ?? null,
    });
    setIsModalOpen(true);
  };

  const handleCreate = () => {
    setEditingUser(null);
    setIsCreating(true);
    setFormData({ name: '', email: '', password: '', role: 'staff', branch_id: '', supplier_id: '', branch_ids: null, supplier_ids: null, permissions: null });
    setIsModalOpen(true);
  };

  const handleSendInvite = async (user) => {
    try {
      const res = await axios.post(`${API}/users/${user.user_id}/send-invite`, {}, { withCredentials: true });
      toast.success(`${res.data.message} to ${user.email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await axios.delete(`${API}/users/${userId}`, { withCredentials: true });
      toast.success('User deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const getRoleBadgeVariant = (role) => {
    switch (role) {
      case 'master_admin': return 'default';
      case 'manager': return 'secondary';
      default: return 'outline';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'master_admin': return 'Master Admin';
      case 'manager': return 'Manager';
      case 'supplier': return 'Supplier';
      default: return 'Staff';
    }
  };

  const getBranchName = (branchId) => branches.find(b => b.branch_id === branchId)?.name || 'Not assigned';
  const getSupplierName = (supplierId) => suppliers.find(s => s.supplier_id === supplierId)?.name || '';

  if (loading) {
    return (
      <SettingsSubPageWrapper title="Users">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </SettingsSubPageWrapper>
    );
  }

  return (
    <SettingsSubPageWrapper title="Users">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
            User Management
          </h1>
          <p className="text-muted-foreground mt-1">Manage user roles and permissions</p>
        </div>
        <Button onClick={handleCreate} data-testid="add-user-btn">
          <Plus className="w-4 h-4 mr-2" />Add User
        </Button>
      </div>

      {/* Role Legend */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />Role Permissions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="p-4 rounded-lg bg-primary/10 border border-primary/20">
              <h3 className="font-semibold text-primary mb-2">Master Admin</h3>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>- Full system access</li>
                <li>- Manage branches & users</li>
                <li>- All manager permissions</li>
              </ul>
            </div>
            <div className="p-4 rounded-lg bg-secondary border border-border">
              <h3 className="font-semibold mb-2">Manager</h3>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>- Manage items & suppliers</li>
                <li>- Create & edit recipes</li>
                <li>- Manage printers</li>
              </ul>
            </div>
            <div className="p-4 rounded-lg bg-muted border border-border">
              <h3 className="font-semibold mb-2">Staff</h3>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>- Perform stock takes</li>
                <li>- View items & recipes</li>
                <li>- View & print orders</li>
              </ul>
            </div>
            <div className="p-4 rounded-lg bg-orange-50 border border-orange-200">
              <h3 className="font-semibold text-orange-700 mb-2">Supplier</h3>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>- View their own orders</li>
                <li>- Limited access only</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Users ({users.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Branch / Supplier</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.user_id} data-testid={`user-row-${user.user_id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={user.picture} alt={user.name} />
                        <AvatarFallback className="bg-primary text-primary-foreground">
                          {user.name?.charAt(0) || 'U'}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium">{user.name}</p>
                        {user.user_id === currentUser?.user_id && (
                          <Badge variant="outline" className="text-xs">You</Badge>
                        )}
                        {!user.has_password && (
                          <Badge variant="secondary" className="text-xs" data-testid={`pending-invite-${user.user_id}`}>Invite pending</Badge>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm">{user.email}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={getRoleBadgeVariant(user.role)}>
                      {getRoleLabel(user.role)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {user.role === 'supplier' && user.supplier_id
                      ? getSupplierName(user.supplier_id)
                      : user.role === 'master_admin'
                        ? 'All Branches'
                        : getBranchName(user.branch_id)
                    }
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => handleSendInvite(user)}
                        title={user.has_password ? 'Send password reset email' : 'Resend invite email'}
                        data-testid={`send-invite-${user.user_id}`}>
                        <Send className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleEdit(user)} data-testid={`edit-user-${user.user_id}`}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      {user.user_id !== currentUser?.user_id && (
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(user.user_id)} data-testid={`delete-user-${user.user_id}`}>
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create / Edit User Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{isCreating ? 'Add New User' : 'Edit User'}</DialogTitle>
            <DialogDescription>Users sign in with their email and password. Leave the password blank on a new user to email them an invite to set their own.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {!isCreating && editingUser && (
              <div className="flex items-center gap-3 p-4 bg-muted rounded-lg">
                <Avatar className="h-12 w-12">
                  <AvatarImage src={editingUser.picture} alt={editingUser.name} />
                  <AvatarFallback className="bg-primary text-primary-foreground">
                    {editingUser.name?.charAt(0) || 'U'}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-medium">{editingUser.name}</p>
                  <p className="text-sm text-muted-foreground">{editingUser.email}</p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Full Name *</Label>
                <Input value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., John Smith" required data-testid="user-name-input" />
              </div>
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input type="email" value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="e.g., john@merchant.co.za" required data-testid="user-email-input" />
              </div>
            </div>

            <div className="space-y-2">
              <Label>{isCreating ? 'Password (optional)' : 'New Password'}</Label>
              <Input type="password" value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder={isCreating ? 'Leave blank to email an invite link' : 'Leave blank to keep current password'}
                minLength={6} autoComplete="new-password"
                data-testid="user-password-input" />
            </div>

            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                <SelectTrigger data-testid="role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="staff">Staff</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="master_admin">Master Admin</SelectItem>
                  <SelectItem value="supplier">Supplier</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {formData.role === 'supplier' ? (
              <div className="space-y-2">
                <Label>Linked Supplier</Label>
                <Select
                  value={formData.supplier_id || 'none'}
                  onValueChange={(value) => setFormData({ ...formData, supplier_id: value === 'none' ? '' : value })}
                >
                  <SelectTrigger data-testid="supplier-select"><SelectValue placeholder="Select supplier" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not linked</SelectItem>
                    {suppliers.map((s) => (
                      <SelectItem key={s.supplier_id} value={s.supplier_id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : formData.role !== 'master_admin' ? (
              <UserAccessFields formData={formData} setFormData={setFormData} branches={branches}
                suppliers={suppliers.filter((x) => !x.archived)} catalog={perms.catalog} rolePerms={perms.roles[formData.role]} />
            ) : null}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="save-user-btn">
                {isCreating ? 'Create User' : 'Update User'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </SettingsSubPageWrapper>
  );
}
